'use client';

import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { createTheme, ThemeOptions } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import HolyLoader from 'holy-loader';
import { NextIntlClientProvider } from 'next-intl';
import type { getMessages, getLocale } from 'next-intl/server';
import { TenantProvider, Tenant } from '@cntxt/domains/access-control';
import { NativeGeolocationHostBootstrap } from '@cntxt/domains/location';
import { NotificationProviderWrapper } from '@cntxt/domains/modules/navigation';
import { NetworkStatusProvider } from '@cntxt/shared/infrastructure/network';
import { QueryClientProvider } from '@cntxt/shared/infrastructure/providers/QueryClientProvider';
import { SessionProvider } from '@cntxt/shared/infrastructure/session';
import { SessionManagement } from '@cntxt/shared/infrastructure/session-management';
import { WebVitals } from '@cntxt/shared/infrastructure/web-vitals';
import { NotificationProvider } from '@cntxt/shared/ui/components';
import { ThemeProvider } from '@cntxt/shared/ui/mui-theme';
import { deepMerge } from '@cntxt/shared/utilities/helpers';
import { defaultTheme } from '@cntxt/shared/utilities/theme';
import { AuthProvider } from './AuthProvider';
type ProvidersProps = {
  children: React.ReactNode;
  messages: Awaited<ReturnType<typeof getMessages>>;
  locale: Awaited<ReturnType<typeof getLocale>>;
  themeOptions: ThemeOptions;
  tenant: Tenant;
};
export function Providers(t0) {
  const $ = _c(24);
  const {
    children,
    messages,
    locale,
    themeOptions,
    tenant
  } = t0;
  let t1;
  if ($[0] !== themeOptions) {
    t1 = createTheme(deepMerge(defaultTheme, themeOptions));
    $[0] = themeOptions;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const theme = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <NativeGeolocationHostBootstrap />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      position: "bottom-right"
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== theme.palette.secondary.main) {
    t4 = <HolyLoader color={theme.palette.secondary.main} height={5} />;
    $[4] = theme.palette.secondary.main;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  let t6;
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <NotificationProvider />;
    t6 = <WebVitals />;
    t7 = <SessionManagement />;
    $[6] = t5;
    $[7] = t6;
    $[8] = t7;
  } else {
    t5 = $[6];
    t6 = $[7];
    t7 = $[8];
  }
  let t8;
  if ($[9] !== children) {
    t8 = <NotificationProviderWrapper><AuthProvider>{children}</AuthProvider></NotificationProviderWrapper>;
    $[9] = children;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t4 || $[12] !== t8) {
    t9 = <LocalizationProvider dateAdapter={AdapterLuxon}><QueryClientProvider reactQueryDevtools={t3}><NetworkStatusProvider>{t4}{t5}{t6}{t7}{t8}</NetworkStatusProvider></QueryClientProvider></LocalizationProvider>;
    $[11] = t4;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t9 || $[15] !== theme) {
    t10 = <SessionProvider><ThemeProvider theme={theme}>{t9}</ThemeProvider></SessionProvider>;
    $[14] = t9;
    $[15] = theme;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== locale || $[18] !== messages || $[19] !== t10) {
    t11 = <NextIntlClientProvider messages={messages} locale={locale}>{t10}</NextIntlClientProvider>;
    $[17] = locale;
    $[18] = messages;
    $[19] = t10;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t11 || $[22] !== tenant) {
    t12 = <TenantProvider tenant={tenant}>{t2}{t11}</TenantProvider>;
    $[21] = t11;
    $[22] = tenant;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  return t12;
}